// src/pages/AdminPatientDiaryPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, Typography, Button, Space, Tabs, Table, Form, Input, InputNumber, 
  DatePicker, Select, TimePicker, message, Modal, Popconfirm
} from 'antd';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { adminAPI } from '../utils/api';
import { useAuth } from '../App';
import dayjs from 'dayjs';
import { decimalInputNumberProps, formatDecimalDisplay } from '../utils/decimalFormat';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

export const AdminPatientDiaryPage: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [patient, setPatient] = useState<any>(null);
  const [glucoseEntries, setGlucoseEntries] = useState<any[]>([]);
  const [weightEntries, setWeightEntries] = useState<any[]>([]);
  const [movementEntries, setMovementEntries] = useState<any[]>([]);
  const [symptomEntries, setSymptomEntries] = useState<any[]>([]);
  const [abdomenEntries, setAbdomenEntries] = useState<any[]>([]);
  const [visitEntries, setVisitEntries] = useState<any[]>([]);
  const [labTestEntries, setLabTestEntries] = useState<any[]>([]);
  const [mealEntries, setMealEntries] = useState<any[]>([]);
  
  const [editingEntry, setEditingEntry] = useState<any>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const loadPatientDetails = useCallback(async () => {
    try {
      const response = await adminAPI.getUserDetails(Number(patientId));
      setPatient(response.data);
    } catch (error: any) {
      message.error('Ошибка загрузки данных пациента');
    }
  }, [patientId]);

  const loadPatientData = useCallback(async () => {
    if (!patientId) return;
    try {
      const params = selectedDate
        ? { from_date: selectedDate, to_date: selectedDate }
        : undefined;
      const [
        glucoseRes,
        weightRes,
        movementRes,
        symptomRes,
        abdomenRes,
        visitRes,
        labTestRes,
        mealRes
      ] = await Promise.all([
        adminAPI.getPatientGlucoseEntries(Number(patientId), params),
        adminAPI.getPatientWeightEntries(Number(patientId), params),
        adminAPI.getPatientMovementEntries(Number(patientId), params),
        adminAPI.getPatientSymptomEntries(Number(patientId), params),
        adminAPI.getPatientAbdomenEntries(Number(patientId), params),
        adminAPI.getPatientVisitEntries(Number(patientId), params),
        adminAPI.getPatientLabTestEntries(Number(patientId), params),
        adminAPI.getPatientMealEntries(Number(patientId), params),
      ]);
      
      setGlucoseEntries((glucoseRes.data || []) as any[]);
      setWeightEntries((weightRes.data || []) as any[]);
      setMovementEntries((movementRes.data || []) as any[]);
      setSymptomEntries((symptomRes.data || []) as any[]);
      setAbdomenEntries((abdomenRes.data || []) as any[]);
      setVisitEntries((visitRes.data || []) as any[]);
      setLabTestEntries((labTestRes.data || []) as any[]);
      setMealEntries((mealRes.data || []) as any[]);
    } catch (error: any) {
      console.error('Error loading patient data:', error);
      message.error(error.response?.data?.detail || 'Ошибка загрузки дневников пациентки');
    }
  }, [patientId, selectedDate]);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/admin');
      return;
    }
    if (patientId) {
      loadPatientDetails();
      loadPatientData();
    }
  }, [patientId, user, navigate, loadPatientDetails, loadPatientData]);

  const handleEdit = (entry: any, type: string) => {
    setEditingEntry({ ...entry, type });
    const formValues: any = { ...entry };
    
    // Convert dates to dayjs
    if (entry.timestamp) formValues.timestamp = dayjs(entry.timestamp);
    if (entry.entry_date) formValues.entry_date = dayjs(entry.entry_date);
    if (entry.movement_time) formValues.movement_time = dayjs(entry.movement_time);
    if (entry.visit_date) formValues.visit_date = dayjs(entry.visit_date);
    if (entry.test_date) formValues.test_date = dayjs(entry.test_date);
    
    editForm.setFieldsValue(formValues);
    setEditModalVisible(true);
  };

  const handleDelete = async (entryId: number, type: string) => {
    try {
      switch (type) {
        case 'glucose':
          await adminAPI.deleteGlucoseEntry(entryId);
          break;
        case 'meal':
          await adminAPI.deleteMealEntry(entryId);
          break;
        case 'weight':
          await adminAPI.deleteWeightEntry(entryId);
          break;
        case 'movement':
          await adminAPI.deleteMovementEntry(entryId);
          break;
        case 'symptom':
          await adminAPI.deleteSymptomEntry(entryId);
          break;
        case 'abdomen':
          await adminAPI.deleteAbdomenEntry(entryId);
          break;
        case 'visit':
          await adminAPI.deleteVisitEntry(entryId);
          break;
        case 'lab_test':
          await adminAPI.deleteLabTestEntry(entryId);
          break;
      }
      message.success('Запись удалена');
      loadPatientData();
    } catch (error: any) {
      message.error('Ошибка удаления записи');
    }
  };

  const handleSave = async (values: any) => {
    try {
      const { type, id } = editingEntry;
      const updateData = { ...values };
      
      // Convert dayjs to string
      if (updateData.timestamp) updateData.timestamp = updateData.timestamp.format('YYYY-MM-DD HH:mm:ss');
      if (updateData.entry_date) updateData.entry_date = updateData.entry_date.format('YYYY-MM-DD');
      if (updateData.movement_time) updateData.movement_time = updateData.movement_time.format('HH:mm:ss');
      if (updateData.visit_date) updateData.visit_date = updateData.visit_date.format('YYYY-MM-DD');
      if (updateData.test_date) updateData.test_date = updateData.test_date.format('YYYY-MM-DD');
      
      switch (type) {
        case 'glucose':
          await adminAPI.updateGlucoseEntry(id, updateData);
          break;
        case 'meal':
          await adminAPI.updateMealEntry(id, updateData);
          break;
        case 'weight':
          await adminAPI.updateWeightEntry(id, updateData);
          break;
        case 'movement':
          await adminAPI.updateMovementEntry(id, updateData);
          break;
        case 'symptom':
          await adminAPI.updateSymptomEntry(id, updateData);
          break;
        case 'abdomen':
          // Handle file upload separately
          const formData = new FormData();
          Object.keys(updateData).forEach(key => {
            if (key !== 'photo' && updateData[key] !== undefined) {
              formData.append(key, updateData[key]);
            }
          });
          if (values.photo && values.photo.file) {
            formData.append('photo', values.photo.file.originFileObj);
          }
          await adminAPI.updateAbdomenEntry(id, formData);
          break;
        case 'visit':
          const visitFormData = new FormData();
          Object.keys(updateData).forEach(key => {
            if (key !== 'photo' && updateData[key] !== undefined) {
              visitFormData.append(key, updateData[key]);
            }
          });
          if (values.photo && values.photo.file) {
            visitFormData.append('photo', values.photo.file.originFileObj);
          }
          await adminAPI.updateVisitEntry(id, visitFormData);
          break;
        case 'lab_test':
          const labFormData = new FormData();
          Object.keys(updateData).forEach(key => {
            if (key !== 'photo' && updateData[key] !== undefined) {
              labFormData.append(key, updateData[key]);
            }
          });
          if (values.photo && values.photo.file) {
            labFormData.append('photo', values.photo.file.originFileObj);
          }
          await adminAPI.updateLabTestEntry(id, labFormData);
          break;
      }
      
      message.success('Запись обновлена');
      setEditModalVisible(false);
      editForm.resetFields();
      loadPatientData();
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Ошибка обновления записи');
    }
  };

  const renderEditForm = () => {
    if (!editingEntry) return null;
    
    const { type } = editingEntry;
    
    switch (type) {
      case 'glucose':
        return (
          <>
            <Form.Item name="timestamp" label="Дата и время" rules={[{ required: true }]}>
              <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="glucose_level" label="Уровень глюкозы (ммоль/л)" rules={[{ required: true }]}>
              <InputNumber min={0} max={30} step={0.1} precision={1} style={{ width: '100%' }} {...decimalInputNumberProps} />
            </Form.Item>
            <Form.Item name="meal_type" label="Тип приема пищи">
              <Select>
                <Option value="before_meal">До еды</Option>
                <Option value="after_meal">После еды</Option>
                <Option value="fasting">Натощак</Option>
              </Select>
            </Form.Item>
            <Form.Item name="notes" label="Примечания">
              <TextArea rows={3} />
            </Form.Item>
          </>
        );
      case 'meal':
        return (
          <>
            <Form.Item name="timestamp" label="Дата и время" rules={[{ required: true }]}>
              <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="food_name" label="Название блюда" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="calories" label="Калории">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="notes" label="Примечания">
              <TextArea rows={3} />
            </Form.Item>
          </>
        );
      case 'weight':
        return (
          <>
            <Form.Item name="entry_date" label="Дата" rules={[{ required: true }]}>
              <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="weight" label="Вес (кг)" rules={[{ required: true }]}>
              <InputNumber min={0} max={200} step={0.1} precision={1} style={{ width: '100%' }} {...decimalInputNumberProps} />
            </Form.Item>
            <Form.Item name="notes" label="Примечания">
              <TextArea rows={3} />
            </Form.Item>
          </>
        );
      case 'movement':
        return (
          <>
            <Form.Item name="entry_date" label="Дата" rules={[{ required: true }]}>
              <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="movement_time" label="Время шевеления">
              <TimePicker format="HH:mm" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="movement_count" label="Количество шевелений">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="notes" label="Примечания">
              <TextArea rows={3} />
            </Form.Item>
          </>
        );
      case 'symptom':
        return (
          <>
            <Form.Item name="entry_date" label="Дата" rules={[{ required: true }]}>
              <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="symptom_type" label="Тип симптома">
              <Select>
                <Option value="nausea">Тошнота</Option>
                <Option value="headache">Головная боль</Option>
                <Option value="back_pain">Боль в спине</Option>
                <Option value="other">Другое</Option>
              </Select>
            </Form.Item>
            <Form.Item name="severity" label="Интенсивность">
              <Select>
                <Option value={1}>Легкая</Option>
                <Option value={2}>Умеренная</Option>
                <Option value={3}>Сильная</Option>
              </Select>
            </Form.Item>
            <Form.Item name="notes" label="Примечания">
              <TextArea rows={3} />
            </Form.Item>
          </>
        );
      default:
        return <Text>Редактирование для этого типа записи пока не реализовано</Text>;
    }
  };

  const glucoseColumns = [
    {
      title: 'Дата',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (text: string) => dayjs(text).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Уровень глюкозы',
      dataIndex: 'glucose_level',
      key: 'glucose_level',
      render: (value: number) => `${formatDecimalDisplay(value)} ммоль/л`,
    },
    {
      title: 'Тип приема пищи',
      dataIndex: 'meal_type',
      key: 'meal_type',
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record, 'glucose')} size="small" />
          <Popconfirm title="Удалить запись?" onConfirm={() => handleDelete(record.id, 'glucose')}>
            <Button icon={<DeleteOutlined />} danger size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const weightColumns = [
    {
      title: 'Дата',
      dataIndex: 'entry_date',
      key: 'entry_date',
      render: (text: string) => dayjs(text).format('DD.MM.YYYY'),
    },
    {
      title: 'Вес',
      dataIndex: 'weight',
      key: 'weight',
      render: (value: number) => `${formatDecimalDisplay(value)} кг`,
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record, 'weight')} size="small" />
          <Popconfirm title="Удалить запись?" onConfirm={() => handleDelete(record.id, 'weight')}>
            <Button icon={<DeleteOutlined />} danger size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'glucose',
      label: 'Глюкоза',
      children: (
        <Table
          columns={glucoseColumns}
          dataSource={glucoseEntries}
          rowKey="id"
          pagination={{ pageSize: 20 }}
        />
      ),
    },
    {
      key: 'meals',
      label: 'Питание',
      children: <Table dataSource={mealEntries} rowKey="id" pagination={{ pageSize: 20 }} />,
    },
    {
      key: 'weight',
      label: 'Вес',
      children: (
        <Table
          columns={weightColumns}
          dataSource={weightEntries}
          rowKey="id"
          pagination={{ pageSize: 20 }}
        />
      ),
    },
    {
      key: 'movements',
      label: 'Шевеления',
      children: <Table dataSource={movementEntries} rowKey="id" pagination={{ pageSize: 20 }} />,
    },
    {
      key: 'symptoms',
      label: 'Симптомы',
      children: <Table dataSource={symptomEntries} rowKey="id" pagination={{ pageSize: 20 }} />,
    },
    {
      key: 'abdomen',
      label: 'Окружность живота',
      children: <Table dataSource={abdomenEntries} rowKey="id" pagination={{ pageSize: 20 }} />,
    },
    {
      key: 'visits',
      label: 'Посещения',
      children: <Table dataSource={visitEntries} rowKey="id" pagination={{ pageSize: 20 }} />,
    },
    {
      key: 'lab_tests',
      label: 'Анализы',
      children: <Table dataSource={labTestEntries} rowKey="id" pagination={{ pageSize: 20 }} />,
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin')}>
          Назад к админке
        </Button>
        <Title level={2} style={{ margin: 0 }}>
          Дневник пациента: {patient?.full_name || 'Загрузка...'}
        </Title>
      </Space>

      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Text>Фильтр по дате:</Text>
          <DatePicker
            format="YYYY-MM-DD"
            onChange={(date) => {
              setSelectedDate(date ? date.format('YYYY-MM-DD') : null);
              loadPatientData();
            }}
            allowClear
          />
        </Space>

        <Tabs items={tabItems} />
      </Card>

      <Modal
        title="Редактировать запись"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          editForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={editForm} layout="vertical" onFinish={handleSave}>
          {renderEditForm()}
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Сохранить
              </Button>
              <Button onClick={() => {
                setEditModalVisible(false);
                editForm.resetFields();
              }}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
